import getMediaElementsByUuids from 'editor/src/store/design/selector/getMediaElementsByUuids';
import { areAllTextElements } from 'editor/src/store/design/util/elementTypeGuards';

import { RootState } from 'editor/src/store';

import { FontDefinition, FontMetadataState } from '../types';

import getCurrentFontStylesProperty from './getCurrentFontStylesProperty';
import getFontDefinition from './getFontDefinition';

export function getCurrentFontMetadataState(state: RootState): FontMetadataState {
  const selectedElements = getMediaElementsByUuids(state, state.editor.selectedElementUuids);

  if (!areAllTextElements(selectedElements)) {
    return {
      isShared: false,
      allMetadata: {},
    };
  }

  const defaultFontDefinition = getFontDefinition(state, state.fonts.defaultFontStyles.fontFamily) as FontDefinition;

  // If only one element, return its font metadata
  if (selectedElements.length === 1) {
    const { fontFamily } = selectedElements[0].extra;
    const fontDefinition = getFontDefinition(state, fontFamily);

    return {
      isShared: true,
      metadata: fontDefinition?.metadata || defaultFontDefinition.metadata,
    };
  }

  // For multiple text elements, check if they share the same font family
  const firstFontFamily = selectedElements[0].extra.fontFamily;
  const allSameFont = selectedElements.every((element) => element.extra.fontFamily === firstFontFamily);

  if (allSameFont) {
    const fontDefinition = getFontDefinition(state, firstFontFamily);

    return {
      isShared: true,
      metadata: fontDefinition?.metadata || defaultFontDefinition.metadata,
    };
  }

  // When fonts are different, collect metadata for each element
  const allMetadata = selectedElements.reduce(
    (acc, element) => {
      const { fontFamily } = element.extra;
      const fontDefinition = getFontDefinition(state, fontFamily);
      acc[element.uuid] = fontDefinition?.metadata || defaultFontDefinition.metadata;
      return acc;
    },
    {} as Record<number, NonNullable<FontDefinition['metadata']>>,
  );

  return {
    isShared: false,
    allMetadata,
  };
}

export function getCurrentFontDefinition(state: RootState): FontDefinition {
  const fontFamily = getCurrentFontStylesProperty(state, 'fontFamily');
  const fontDefinition = getFontDefinition(state, fontFamily);

  if (!fontDefinition) {
    return getFontDefinition(state, state.fonts.defaultFontStyles.fontFamily) as FontDefinition;
  }

  return fontDefinition;
}
